/* eslint-disable react/prop-types */
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Chip,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/joy';
import { Add, Edit } from '@mui/icons-material';
import PermissionGate from '../../../main/components/PermissionGate';
import { PERMISSIONS } from '../../../main/context/AuthContext';
import { usePermissions } from '../../../main/hooks/usePermissions';
import { listShipmentUnitPricingRules } from '../../../main/API/shipmentUnit';
import { getPaginatedResults } from '../../../main/utils/pagination';
import { masterApiErrorMessage } from '../../../main/utils/masterApiError';

function activeChipColor(active) {
  return active ? 'success' : 'neutral';
}

export default function UnitPricingRulesPanel({ unit, onAddRule, onRecalcPrompt }) {
  const { t } = useTranslation('ui');
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState('');

  const unitId = unit?.id;

  const fetchRules = useCallback(async () => {
    if (!unitId || !hasPermission(PERMISSIONS.PRICING_RULES_LIST)) {
      setRules([]);
      return;
    }
    setLoading(true);
    setLoadError('');
    try {
      const res = await listShipmentUnitPricingRules(unitId);
      setRules(getPaginatedResults(res?.data));
    } catch (err) {
      setRules([]);
      setLoadError(masterApiErrorMessage(err, t));
    } finally {
      setLoading(false);
    }
  }, [unitId, hasPermission, t]);

  useEffect(() => {
    fetchRules();
  }, [fetchRules]);

  if (!hasPermission(PERMISSIONS.PRICING_RULES_LIST)) {
    return null;
  }

  const handleAddRule = () => {
    if (!unitId || !hasPermission(PERMISSIONS.PRICING_RULES_CREATE)) return;
    if (typeof onAddRule === 'function') {
      onAddRule(unit);
      return;
    }
    navigate('/PricingRules/Add', {
      state: {
        unitContext: {
          id: unitId,
          serial_number: unit.serial_number ?? null,
        },
        returnTo: '/shipment/units',
      },
    });
  };

  return (
    <Box sx={{ mt: 2 }}>
      <DividerPlaceholder />
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
        <Typography level="title-sm">{t('pricingRules.unitRulesSectionTitle')}</Typography>
        <PermissionGate permission={PERMISSIONS.PRICING_RULES_CREATE}>
          <Button size="sm" variant="soft" startDecorator={<Add />} onClick={handleAddRule}>
            {t('pricingRules.unitRulesAdd')}
          </Button>
        </PermissionGate>
      </Stack>
      <Typography level="body-xs" sx={{ color: 'neutral.600', mb: 1 }}>
        {t('pricingRules.unitRulesSectionHint')}
      </Typography>

      {loadError ? (
        <Typography level="body-sm" color="danger" sx={{ mb: 1 }}>
          {loadError}
        </Typography>
      ) : null}

      {loading ? (
        <Typography level="body-sm">{t('actions.loading')}</Typography>
      ) : rules.length === 0 ? (
        <Typography level="body-sm">{t('pricingRules.unitRulesEmpty')}</Typography>
      ) : (
        <Stack spacing={0.75}>
          {rules.map((rule) => (
            <Box
              key={rule.id}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                flexWrap: 'wrap',
                p: 1,
                bgcolor: 'background.level1',
                borderRadius: 'sm',
              }}
            >
              <Typography level="body-sm" fontWeight="lg" sx={{ minWidth: 120 }}>
                {rule.code}
              </Typography>
              <Chip size="sm" variant="soft">
                {rule.scope}
              </Chip>
              <Chip size="sm" variant="outlined" color={activeChipColor(rule.active)}>
                {rule.active ? t('serviceManager.priceYes') : t('serviceManager.priceNo')}
              </Chip>
              <Typography level="body-xs" sx={{ color: 'neutral.600', flex: 1 }}>
                {rule.strategy}
              </Typography>
              <PermissionGate permission={PERMISSIONS.PRICING_RULES_UPDATE}>
                <Tooltip title={t('pricingRules.tooltipEdit')}>
                  <IconButton
                    size="sm"
                    variant="plain"
                    onClick={() => window.open(`/PricingRules/${rule.id}/Edit`, '_blank')}
                  >
                    <Edit fontSize="small" />
                  </IconButton>
                </Tooltip>
              </PermissionGate>
            </Box>
          ))}
        </Stack>
      )}

      {typeof onRecalcPrompt === 'function' && rules.some((r) => r.active) ? (
        <Typography level="body-xs" sx={{ color: 'neutral.600', mt: 1 }}>
          {t('pricingRules.unitRulesRecalcHint')}
        </Typography>
      ) : null}
    </Box>
  );
}

function DividerPlaceholder() {
  return (
    <Box
      sx={{
        borderTop: '1px solid',
        borderColor: 'divider',
        my: 1.5,
      }}
    />
  );
}
